import React from 'react';
import { FileTextIcon, DownloadIcon, BriefcaseIcon, GraduationCapIcon, AwardIcon, GlobeIcon } from 'lucide-react';
export const Resume = () => {
  // Timeline data for experience and education
  const experienceData = [{
    year: 'August 2021 - August 2025',
    title: '3D Animator',
    company: 'The Odd Gentlemen, Remote',
    description: 'Created expressive character animations using keyframe and motion capture workflows in Maya and Blender. Worked on Ghostbusters: The Video Game Remastered as an external partner – contributed to gameplay animation and received direct praise. Collaborated with mocap teams and game designers. Contributed to layout and previs for cinematic scenes. Focused on acting, timing, and body language.'
  }, {
    year: 'October 2020 - June 2021',
    title: 'Layout Artist / 3D Animator',
    company: 'Wilfilm A/S, Copenhagen',
    description: "Produced previs sequences and animation for children's series and feature film. Projects: Novelmore, Adventures of Ayuma, Little Allan – The Human Antenna. Recognized for fast learning and productivity."
  }, {
    year: 'June 2021 - November 2021',
    title: '3D Animator Consulting',
    company: 'Paramount Animation, Remote',
    description: 'Assisted the animation department with layout blocking, acting tests, and cleanup. Contributed to early-stage character animation and shot planning during pre-production. Worked closely with senior animators and supervisors, learning professional workflows in a feature film pipeline.'
  }];
  const educationData = [{
    year: 'August 2016 - January 2020',
    degree: 'Bachelor in Character Animation',
    institution: 'The Animation Workshop, Viborg',
    description: 'Focus: 3D character animation, acting, storytelling, layout, visual development, rigging. Final grade: 12 (equivalent to A / highest mark)'
  }];
  // Professional highlights
  const highlights = ['Contributed to Ghostbusters: The Video Game Remastered as an external animator; received direct thanks from the AAA partner studio for animation quality and efficiency.', 'Described by colleagues at The Odd Gentlemen as a "unique asset" to the team for combining strong animation skills with deep nonverbal communication insight.', 'Selected to represent the studio and participate in creative workshops on-site in Los Angeles.', "Graduated with top marks (12 on the Danish 7-point scale) from The Animation Workshop's Character Animation program."];
  // Languages
  const languages = [{
    language: 'Danish',
    proficiency: 'Native'
  }, {
    language: 'English',
    proficiency: 'Fluent'
  }, {
    language: 'Danish Sign Language',
    proficiency: 'Native'
  }, {
    language: 'American Sign Language',
    proficiency: 'Conversational'
  }];
  return <section id="resume" className="py-20 bg-gray-900" data-id="element-162">
      <div className="container mx-auto px-4" data-id="element-163">
        <h2 className="text-4xl font-bold mb-2 text-center" data-id="element-164">Resume</h2>
        <p className="text-gray-400 text-center mb-6" data-id="element-165">
          My professional journey
        </p>
        <div className="flex justify-center mb-12" data-id="element-166">
          <button className="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-lg transition-colors" data-id="element-167">
            <FileTextIcon size={20} className="mr-2" data-id="element-168" />
            Download Full CV
            <DownloadIcon size={16} className="ml-2" data-id="element-169" />
          </button>
        </div>
        {/* Highlights Section */}
        <div className="mb-16" data-id="element-170">
          <h3 className="text-2xl font-bold mb-8 flex items-center" data-id="element-171">
            <span className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center mr-3" data-id="element-172">
              <AwardIcon size={20} data-id="element-173" />
            </span>
            Professional Highlights
          </h3>
          <div className="bg-gray-800/50 p-6 rounded-lg" data-id="element-174">
            <ul className="space-y-4" data-id="element-175">
              {highlights.map((highlight, index) => <li key={index} className="flex" data-id="element-176">
                  <div className="mr-3 mt-1 text-indigo-400" data-id="element-177">•</div>
                  <p className="text-gray-300" data-id="element-178">{highlight}</p>
                </li>)}
            </ul>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-179">
          {/* Professional Experience */}
          <div data-id="element-180">
            <h3 className="text-2xl font-bold mb-8 flex items-center" data-id="element-181">
              <span className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center mr-3" data-id="element-182">
                <BriefcaseIcon size={20} data-id="element-183" />
              </span>
              Professional Experience
            </h3>
            <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-1/2 before:h-full before:w-0.5 before:bg-gray-700" data-id="element-184">
              {experienceData.map((item, index) => <div key={index} className="relative pl-12" data-id="element-185">
                  <div className="absolute left-0 top-1 w-10 h-10 rounded-full bg-gray-800 border-4 border-gray-700 flex items-center justify-center" data-id="element-186">
                    <div className="w-3 h-3 bg-indigo-500 rounded-full" data-id="element-187"></div>
                  </div>
                  <div className="bg-gray-800/50 p-6 rounded-lg" data-id="element-188">
                    <span className="text-indigo-400 text-sm font-medium" data-id="element-189">
                      {item.year}
                    </span>
                    <h4 className="text-xl font-bold mt-1" data-id="element-190">{item.title}</h4>
                    <p className="text-gray-400 mt-1" data-id="element-191">{item.company}</p>
                    <p className="text-gray-300 mt-3" data-id="element-192">{item.description}</p>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Education */}
          <div data-id="element-193">
            <h3 className="text-2xl font-bold mb-8 flex items-center" data-id="element-194">
              <span className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center mr-3" data-id="element-195">
                <GraduationCapIcon size={20} data-id="element-196" />
              </span>
              Education & Training
            </h3>
            <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-1/2 before:h-full before:w-0.5 before:bg-gray-700" data-id="element-197">
              {educationData.map((item, index) => <div key={index} className="relative pl-12" data-id="element-198">
                  <div className="absolute left-0 top-1 w-10 h-10 rounded-full bg-gray-800 border-4 border-gray-700 flex items-center justify-center" data-id="element-199">
                    <div className="w-3 h-3 bg-indigo-500 rounded-full" data-id="element-200"></div>
                  </div>
                  <div className="bg-gray-800/50 p-6 rounded-lg" data-id="element-201">
                    <span className="text-indigo-400 text-sm font-medium" data-id="element-202">
                      {item.year}
                    </span>
                    <h4 className="text-xl font-bold mt-1" data-id="element-203">{item.degree}</h4>
                    <p className="text-gray-400 mt-1" data-id="element-204">{item.institution}</p>
                    <p className="text-gray-300 mt-3" data-id="element-205">{item.description}</p>
                  </div>
                </div>)}
            </div>
            {/* Languages Section */}
            <div className="mt-10" data-id="element-206">
              <h3 className="text-xl font-bold mb-4 flex items-center" data-id="element-207">
                <GlobeIcon size={20} className="mr-2 text-indigo-400" data-id="element-208" />
                Languages
              </h3>
              <div className="bg-gray-800/50 p-6 rounded-lg" data-id="element-209">
                <ul className="space-y-3" data-id="element-210">
                  {languages.map((lang, index) => <li key={index} className="flex justify-between" data-id="element-211">
                      <span className="text-gray-300" data-id="element-212">{lang.language}</span>
                      <span className="text-indigo-400" data-id="element-213">
                        {lang.proficiency}
                      </span>
                    </li>)}
                </ul>
              </div>
            </div>
            {/* Web & Portfolio Links */}
            <div className="mt-10" data-id="element-214">
              <h3 className="text-xl font-bold mb-4" data-id="element-215">Web & Portfolio</h3>
              <div className="bg-gray-800/50 p-6 rounded-lg" data-id="element-216">
                <ul className="space-y-3 text-gray-300" data-id="element-217">
                  <li data-id="element-218">
                    <span className="font-semibold text-indigo-400" data-id="element-219">
                      Website:
                    </span>{' '}
                    <a href="https://sparre.website" className="hover:text-indigo-400 transition-colors" data-id="element-220">
                      sparre.website
                    </a>
                  </li>
                  <li data-id="element-221">
                    <span className="font-semibold text-indigo-400" data-id="element-222">
                      LinkedIn:
                    </span>{' '}
                    <a href="https://linkedin.com/in/søren-bro-sparre-6a197295" className="hover:text-indigo-400 transition-colors" data-id="element-223">
                      linkedin.com/in/søren-bro-sparre-6a197295
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        {/* Skills */}
        <div className="mt-16" data-id="element-224">
          <h3 className="text-2xl font-bold mb-8 text-center" data-id="element-225">
            Skills & Expertise
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-226">
            {[{
            category: 'Animation',
            skills: ['3D Character Animation', 'Facial & Body Acting', 'Stylized Animation', 'Realistic Animation']
          }, {
            category: 'Pre-Production',
            skills: ['Layout & Previsualization', 'Storyboarding', 'Visual Development', 'Shot Planning']
          }, {
            category: 'Software & Tools',
            skills: ['Maya', 'Blender', 'Unity Engine', 'Motion Capture Integration']
          }, {
            category: 'Production Skills',
            skills: ['Animation Pipeline', 'Team Collaboration', 'Feature Film Workflow', 'Game Animation']
          }].map((group, index) => <div key={index} className="bg-gray-800/50 p-6 rounded-lg" data-id="element-227">
                <h4 className="text-xl font-bold mb-4 text-indigo-400" data-id="element-228">
                  {group.category}
                </h4>
                <ul className="space-y-2" data-id="element-229">
                  {group.skills.map((skill, idx) => <li key={idx} className="flex items-center" data-id="element-230">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full mr-2" data-id="element-231"></div>
                      <span className="text-gray-300" data-id="element-232">{skill}</span>
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
};